<template>
  <div class="search-destination">
    <Input
      id="destinationStation"
      :label="$t('travelPlanner.search.destination')"
      :internalLabel="true"
      v-model="inputValue"
      :placeholder="$t('travelPlanner.search.destination')"
      :icon="marker"
      :hasDelete="true"
      :suggestions="filteredSuggestions"
      @focus="fetchDestinationSuggestions"
      @select-suggestion="handleSelectSuggestion" />
  </div>
</template>

<script setup lang="ts">
  import { ref, computed, watch } from 'vue'
  import { useTravelSearchStore } from '@/stores/travelSearchStore'
  import { getSortedStationSuggestions } from '@/utils/stationSortingUtils'
  import { findStation, getSearchStationCode } from '@/utils/stationAgglomeration'
  import { pushInteractionToDatalayer } from '@/services/gaLoggingService'
  import Input from '@/components/BaseComponents/Input.vue'
  import marker from '@/assets/icons/marker.svg'
  import { getSearchHistory, addToSearchHistory } from '@/composables/useSearchHistory'

  defineOptions({
    name: 'SearchDestination',
  })

  const travelSearchStore = useTravelSearchStore()
  const inputValue = ref(travelSearchStore.destinationStation.name || '')
  const showSuggestions = ref(false)
  const selectedUICCode = ref('')

  const fetchDestinationSuggestions = () => {
    // Only enable suggestions if we have an origin station
    if (travelSearchStore.OriginStation._u_i_c_station_code) {
      showSuggestions.value = true
    } else {
      // Show a message or handle the case where origin isn't selected
      inputValue.value = ''
    }
  }

  const handleSelectSuggestion = (code: string) => {
    selectedUICCode.value = code
    showSuggestions.value = false
    // A connected station missing from the station list is searched by its code.
    const name =
      findStation(code, travelSearchStore.allStations)?.name ??
      getSearchHistory('destinationSearchHistory').find((h) => h.code === code)?.name ??
      code
    travelSearchStore.setDestinationStation({ name, _u_i_c_station_code: code })
    const { name: destinationName, _u_i_c_station_code: searchCode } =
      travelSearchStore.destinationStation
    travelSearchStore.setOriginDestination(travelSearchStore.origin, destinationName)
    inputValue.value = destinationName
    addToSearchHistory('destinationSearchHistory', { name: destinationName, code: searchCode })
    pushInteractionToDatalayer('add destination', { selection: destinationName })
  }

  const filteredSuggestions = computed(() => {
    const { allStations, OriginStation, destinationSuggestionStations } = travelSearchStore
    // A stop in the origin's own agglomeration is not a destination from it.
    const excludeCode = getSearchStationCode(OriginStation, allStations)
    if (!inputValue.value) {
      if (!OriginStation._u_i_c_station_code) return []
      return getSearchHistory('destinationSearchHistory').filter((h) => h.code !== excludeCode)
    }
    return getSortedStationSuggestions(destinationSuggestionStations, inputValue.value, {
      allStations,
      excludeCode,
    })
  })

  watch(inputValue, (newVal) => {
    if (!newVal) {
      showSuggestions.value = false
      travelSearchStore.setOriginDestination(travelSearchStore.origin, '')
    } else {
      showSuggestions.value = true
    }
  })

  watch(
    () => travelSearchStore.destinationStation,
    (newDestinationStation) => {
      if (newDestinationStation) {
        inputValue.value = newDestinationStation.name
        travelSearchStore.setOriginDestination(travelSearchStore.origin, newDestinationStation.name)
      }
    },
    { immediate: true },
  )
</script>
